import { buildCanonicalCase, defaultCaseDraft } from "../domain/case/case-adapter";
import type { CanonicalCase, CaseDraft } from "../domain/case/canonical-case";

export const DEMO_CASE_COUNT = 2_605;

const trainingYearCounts = [210, 255, 295, 320, 345, 370, 395, 403, 12] as const;

const hospitals = [
  "Northbank University Hospital",
  "Westfield District Hospital",
  "Riverside Children's Hospital",
  "St Cuthbert's Women's Centre",
] as const;

const caseMix = [
  ["Laparoscopic cholecystectomy", "General"],
  ["Total hip replacement", "Orthopaedics"],
  ["Emergency laparotomy", "General"],
  ["Caesarean section", "Obstetrics"],
  ["Tonsillectomy", "ENT"],
  ["Craniotomy", "Neurosurgery"],
  ["Cystoscopy", "Urology"],
  ["Open reduction internal fixation", "Trauma"],
  ["Coronary artery bypass graft", "Cardiac"],
  ["MRI under general anaesthesia", "Paediatrics"],
  ["Carotid endarterectomy", "Vascular"],
  ["Video-assisted thoracoscopic surgery", "Thoracic"],
  ["Wide local excision", "Breast"],
  ["ERCP", "Hepatobiliary"],
  ["Vitrectomy", "Ophthalmics"],
] as const;

const anaestheticTypes = ["GA-Volatile", "GA-TIVA", "GA-Combined", "Regional & Sedation"] as const;
const airways = ["ETT", "i-gel", "LMA", "VL ETT", "Patient's own airway"] as const;
const ages = ["18-59 yrs", "60-79 yrs", "18-59 yrs", "60-79 yrs", "≥80 yrs", "10-17 yrs"] as const;
const asaGrades = ["1", "2", "2", "3", "3", "4"] as const;
const urgencies = ["Elective", "Elective", "Elective", "Urgent", "Expedited", "Immediate"] as const;
const regionalTechniques = ["Spinal", "TAP", "Adductor canal", "Epidural", "Interscalene"] as const;

function supervisionFor(trainingYear: number, index: number): string {
  const progression = [
    ["1", "1", "2a", "2a", "2b"],
    ["1", "2a", "2a", "2b", "3"],
    ["2a", "2b", "2b", "3", "3"],
    ["2b", "2b", "3", "3", "4"],
    ["2b", "3", "3", "4", "4"],
    ["3", "3", "4", "4", "4"],
    ["3", "4", "4", "4", "4"],
    ["3", "4", "4", "4", "4"],
    ["3", "4", "4", "4", "4"],
  ] as const;
  return progression[trainingYear]?.[index % 5] ?? "4";
}

function dateFor(trainingYear: number, index: number, count: number): Date {
  const start = Date.UTC(2018 + trainingYear, 7, 1, 12);
  if (trainingYear === trainingYearCounts.length - 1) {
    return new Date(start + (index % 3) * 86_400_000);
  }
  const offset = Math.min(364, Math.floor(index * 365 / count) + index % 3);
  return new Date(start + offset * 86_400_000);
}

function isoDate(value: Date): string {
  return value.toISOString().slice(0, 10);
}

function demoDraft(trainingYear: number, index: number, globalIndex: number): CaseDraft {
  const mix = caseMix[(globalIndex * 7 + trainingYear * 3) % caseMix.length]!;
  const date = dateFor(trainingYear, index, trainingYearCounts[trainingYear] ?? 1);
  const draft = defaultCaseDraft(date, hospitals[(globalIndex + trainingYear) % hospitals.length]);
  const supervision = supervisionFor(trainingYear, globalIndex);
  const regional = globalIndex % 5 === 0
    ? [regionalTechniques[(globalIndex + trainingYear) % regionalTechniques.length]!]
    : [];
  const missingAsa = globalIndex % 97 === 0;
  const missingSupervision = globalIndex % 151 === 0;
  const missingAge = globalIndex % 173 === 0;

  return {
    ...draft,
    date: isoDate(date),
    session: ["Morning", "Afternoon", "Evening", "Night"][globalIndex % 4] ?? "Morning",
    procedure: mix[0],
    spec: mix[1],
    age: missingAge ? "" : ages[(globalIndex * 5) % ages.length]!,
    asa: missingAsa ? "" : asaGrades[(globalIndex * 3 + trainingYear) % asaGrades.length]!,
    urg: urgencies[(globalIndex + trainingYear) % urgencies.length]!,
    duration: String(35 + (globalIndex * 17) % 245),
    role: trainingYear < 2 ? "Supervised" : globalIndex % 11 === 0 ? "Supervisor" : "Primary",
    maint: anaestheticTypes[(globalIndex + trainingYear) % anaestheticTypes.length]!,
    anaesthetic_equipment: globalIndex % 9 === 0 ? "Videolaryngoscope" : "Standard",
    breathing: globalIndex % 6 === 0 ? "Spontaneous" : "IPPV",
    airway: airways[(globalIndex * 3) % airways.length]!,
    airway_awake_status: "Asleep",
    regional,
    access_new: globalIndex % 7 === 0 ? ["Arterial line"] : ["Peripheral IV"],
    position: ["Supine", "Lateral", "Lithotomy", "Prone"][globalIndex % 4] ?? "Supine",
    dayCase: globalIndex % 3 === 0 ? "Yes" : "No",
    postop: globalIndex % 13 === 0 ? "HDU" : globalIndex % 3 === 0 ? "Day surgery" : "Ward",
    complications: globalIndex % 113 === 0 ? ["CVS instability"] : [],
    sup: missingSupervision ? "" : supervision,
    supGrade: supervision === "4" ? "" : "Consultant",
    teaching: globalIndex % 8 === 0 ? ["Novice"] : [],
    funding: "NHS",
    insurer: "Not applicable",
    notes: "",
  };
}

/**
 * Produces a deterministic, entirely fictional case mix in memory. No source
 * account, clinical note, patient identifier, or imported row is included.
 */
export function createDemoCases(): CanonicalCase[] {
  const output: CanonicalCase[] = [];
  let globalIndex = 0;
  trainingYearCounts.forEach((count, trainingYear) => {
    for (let index = 0; index < count; index += 1) {
      const draft = demoDraft(trainingYear, index, globalIndex);
      const timestamp = new Date(`${draft.date}T12:00:00.000Z`);
      output.push(buildCanonicalCase(draft, {
        id: `sample-case-${String(globalIndex + 1).padStart(4, "0")}`,
        now: timestamp,
        deviceId: "sample-workspace",
        sourceSystem: "LogBookAnaes Studio sample workspace",
        entryOrigin: "developer_generated",
      }));
      globalIndex += 1;
    }
  });
  return output.reverse();
}
